import { Flex, Pagination } from "antd";
import type { ReactNode } from "react";
import { cn } from "../utils/global.utils";
import { enToBnNumber } from "../utils/number.utils";

interface CustomPaginationProps {
  limit: number;
  total: number;
  page?: number;
  hidePagination?: boolean;
  hidePage?: boolean;
  className?: string;
  handlePagination?: (paginationPage: number, paginationLimit: number) => void;
  prevIcon?: ReactNode;
  nextIcon?: ReactNode;
  paginationClassName?: string;
}

export default function CustomPagination({
  limit,
  total,
  page,
  hidePagination,
  hidePage,
  className = "",
  handlePagination,
  prevIcon = "Previous",
  nextIcon = "Next",
  paginationClassName,
}: CustomPaginationProps) {
  const safePage = page || 1; // ensure page starts at 1
  const start = (safePage - 1) * limit + 1;
  const end = Math.min(safePage * limit, total);

  if (!hidePagination && total && limit)
    return (
      <Flex
        gap={8}
        wrap={true}
        align="center"
        justify={hidePage ? "center" : "space-between"}
        className={cn(
          "custom-pagination px-4 py-2.5 mt-2",
          "flex-col sm:flex-row",
          className
        )}
      >
        {!hidePage && (
          <Flex
            align="center"
            gap={8}
            className="text-sm text-white tracking-wide"
          >
            Showing {start} to {end} of {total} entries
          </Flex>
        )}

        {/* Ant Design Pagination */}
        <Pagination
          total={total}
          current={page}
          pageSize={limit}
          showSizeChanger={false}
          onChange={handlePagination}
          className={cn(paginationClassName, "mobile-pagination")}
          itemRender={(pageNumber, type, originalElement) => {
            if (type === "prev")
              return <span className="prev-icon px-3 py-1.5">{prevIcon}</span>;

            if (type === "next")
              return <span className="next-icon px-3 py-1.5">{nextIcon}</span>;

            // hide page numbers on mobile
            if (type === "page")
              return (
                <span className="hidden sm:inline">
                  {enToBnNumber(pageNumber)}
                </span>
              );

            return originalElement;
          }}
        />
      </Flex>
    );

  return null;
}
